import React, { useState, useEffect } from 'react';
import { useWeb3 } from '../context/Web3Context';
import { useSearchParams } from 'react-router-dom';
import { RankBadge } from '../components/RankBadge';
import { getPublicOrigin } from '../lib/publicUrl';
import { formatAddress } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Skeleton } from '../components/ui/skeleton';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { 
    Coins,
    TrendingDown,
    Gift,
    ShoppingBag,
    Trophy,
    Zap,
    Clock,
    ArrowRight,
    Sparkles,
    Crown,
    Target,
    BarChart3,
    History,
    Flame,
    Copy,
    UserPlus,
    Check,
    Share2,
    ArrowDownUp,
    ExternalLink,
    Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// B4P Logo Component
function B4PLogo({ size = 'md' }) {
    const sizes = {
        sm: 'w-6 h-6 text-xs',
        md: 'w-10 h-10 text-sm',
        lg: 'w-14 h-14 text-lg'
    };
    
    return (
        <div className={`${sizes[size]} rounded-full bg-gradient-to-br from-[#FFD700] to-[#FF6B00] flex items-center justify-center font-bold text-white shadow-lg shadow-[#FFD700]/20`}>
            B4P
        </div>
    );
}

// Balance Card
function BalanceCard({ balance, totalEarned, totalSpent, onClaimDaily }) {
    return (
        <Card className="bg-gradient-to-br from-[#FFD700]/10 to-[#FF6B00]/10 border-[#FFD700]/30 animate-fade-in-up">
            <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                        <B4PLogo size="lg" />
                        <div>
                            <div className="text-white/50 text-sm">Your balance</div>
                            <div className="text-4xl font-bold text-white stat-number">
                                {balance.toLocaleString()} <span className="text-[#FFD700] text-2xl">B4P</span>
                            </div>
                        </div>
                    </div>
                    <Button 
                        onClick={onClaimDaily}
                        className="bg-[#00FF66] text-[#0A0A0C] hover:bg-[#00CC52] btn-press"
                        data-testid="claim-daily-btn"
                    >
                        <Gift className="w-4 h-4 mr-2" />
                        Daily Bonus
                    </Button>
                </div>
                
                <div className="grid grid-cols-2 gap-4 mt-4">
                    <div className="p-3 rounded-lg bg-white/5">
                        <div className="text-white/40 text-xs mb-1">Total Earned</div>
                        <div className="text-[#00FF66] font-bold">+{totalEarned.toLocaleString()} B4P</div>
                    </div>
                    <div className="p-3 rounded-lg bg-white/5">
                        <div className="text-white/40 text-xs mb-1">Total Spent</div>
                        <div className="text-[#FF6B6B] font-bold">-{totalSpent.toLocaleString()} B4P</div>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}

// Halving Progress Card
function HalvingCard({ halvingInfo }) {
    const { current_phase, current_multiplier, total_distributed, next_halving_at, progress_to_halving } = halvingInfo;
    
    return (
        <Card className="bg-white/[0.02] border-white/5">
            <CardHeader className="pb-2">
                <CardTitle className="text-lg text-white flex items-center gap-2">
                    <TrendingDown className="w-5 h-5 text-[#FF6B00]" />
                    Halving B4P
                </CardTitle>
                <CardDescription className="text-white/50">
                    Rewards decrease over time
                </CardDescription>
            </CardHeader>
            <CardContent>
                <div className="space-y-4">
                    <div className="flex items-center justify-between">
                        <span className="text-white/60">Current Phase</span>
                        <Badge className="bg-[#FFD700]/20 text-[#FFD700]">
                            Phase {current_phase}
                        </Badge>
                    </div>
                    
                    <div className="flex items-center justify-between">
                        <span className="text-white/60">Multiplier</span>
                        <span className="text-white font-bold">x{current_multiplier}</span>
                    </div>
                    
                    <div className="flex items-center justify-between">
                        <span className="text-white/60">Total Distributed</span>
                        <span className="text-[#00FF66] font-bold">{total_distributed.toLocaleString()} B4P</span>
                    </div>
                    
                    {next_halving_at && (
                        <div>
                            <div className="flex items-center justify-between text-sm mb-2">
                                <span className="text-white/40">Next Halving</span>
                                <span className="text-white/60">{next_halving_at.toLocaleString()} B4P</span>
                            </div>
                            <Progress value={progress_to_halving} className="h-2 bg-white/10" />
                            <div className="text-xs text-white/40 mt-1 text-right">
                                {progress_to_halving}% to next halving
                            </div>
                        </div>
                    )}
                    
                    <div className="p-3 rounded-lg bg-[#FF6B00]/10 border border-[#FF6B00]/20">
                        <div className="flex items-center gap-2 text-[#FF6B00] text-sm">
                            <Flame className="w-4 h-4" />
                            <span>Earn now before rewards decrease!</span>
                        </div>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}

// Shop Item Card
function ShopItemCard({ item, onBuy, userBalance, isPurchasing }) {
    const canAfford = userBalance >= item.cost;
    const hasDiscount = item.base_cost && item.base_cost > item.cost;
    
    const effectIcons = {
        fee_discount: Target,
        ai_access: Sparkles,
        badge: Crown,
        stats_access: BarChart3,
        leaderboard_multiplier: Zap
    };
    
    const Icon = effectIcons[item.effect] || Gift;
    
    return (
        <Card className={`bg-white/[0.02] border-white/5 hover:border-[#FFD700]/30 transition-all card-hover ${!canAfford ? 'opacity-50' : ''}`}>
            <CardContent className="p-4">
                <div className="flex items-start gap-3">
                    <div className="w-12 h-12 rounded-xl bg-[#FFD700]/10 flex items-center justify-center shrink-0">
                        <Icon className="w-6 h-6 text-[#FFD700]" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <h4 className="text-white font-semibold">{item.name}</h4>
                        <p className="text-white/50 text-sm mt-1">{item.description}</p>
                        {item.duration_days > 0 && (
                            <div className="flex items-center gap-1 mt-2 text-xs text-white/40">
                                <Clock className="w-3 h-3" />
                                Duration: {item.duration_days} days
                            </div>
                        )}
                    </div>
                </div>
                
                <div className="flex items-center justify-between mt-4">
                    <div className="flex items-center gap-2">
                        <B4PLogo size="sm" />
                        <span className="text-[#FFD700] font-bold">{item.cost}</span>
                        {hasDiscount && (
                            <span className="text-white/30 text-xs line-through">{item.base_cost}</span>
                        )}
                    </div>
                    <Button
                        size="sm"
                        onClick={() => onBuy(item.id)}
                        disabled={!canAfford || isPurchasing}
                        className={`btn-press ${canAfford ? 'bg-[#FFD700] text-[#0A0A0C] hover:bg-[#FFE44D]' : 'bg-white/10 text-white/40'}`}
                        data-testid={`buy-${item.id}`}
                    >
                        {isPurchasing ? 'Buying...' : canAfford ? 'Buy' : 'Insufficient'}
                    </Button>
                </div>
            </CardContent>
        </Card>
    );
}

// Rewards Info Card
function RewardsCard({ rewards, multiplier }) {
    const rewardLabels = {
        place_bet: 'Place a Bet',
        win_bet: 'Win a Bet',
        daily_login: 'Daily Login',
        referral: 'Referral',
        welcome_bonus: 'Welcome Bonus',
        first_bet: 'First Bet',
        streak_3: '3 Win Streak',
        streak_5: '5 Win Streak',
        streak_10: '10 Win Streak',
        monthly_top1: '#1 Monthly',
        monthly_top2: '#2 Monthly',
        monthly_top3: '#3 Monthly',
        monthly_top10: 'Top 10 Monthly'
    };
    
    return (
        <Card className="bg-white/[0.02] border-white/5">
            <CardHeader className="pb-2">
                <CardTitle className="text-lg text-white flex items-center gap-2">
                    <Gift className="w-5 h-5 text-[#00FF66]" />
                    B4P Rewards
                </CardTitle>
                <CardDescription className="text-white/50">
                    Current multiplier: x{multiplier}
                </CardDescription>
            </CardHeader>
            <CardContent>
                <div className="space-y-2">
                    {Object.entries(rewards).slice(0, 6).map(([action, amount]) => (
                        <div key={action} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                            <span className="text-white/60 text-sm">{rewardLabels[action] || action}</span>
                            <Badge className="bg-[#00FF66]/20 text-[#00FF66]">
                                +{amount} B4P
                            </Badge>
                        </div>
                    ))}
                </div>
            </CardContent>
        </Card>
    );
}

// Leaderboard Component
function B4PLeaderboard({ leaderboard, currentAddress }) {
    return (
        <Card className="bg-white/[0.02] border-white/5">
            <CardHeader className="pb-2">
                <CardTitle className="text-lg text-white flex items-center gap-2">
                    <Trophy className="w-5 h-5 text-[#FFD700]" />
                    Top Holders B4P
                </CardTitle>
            </CardHeader>
            <CardContent>
                {leaderboard.length === 0 ? (
                    <p className="text-white/40 text-center py-4">No data yet</p>
                ) : (
                    <div className="space-y-2">
                        {leaderboard.slice(0, 10).map((entry, idx) => {
                            const isCurrentUser = entry.address === currentAddress?.toLowerCase();
                            
                            return (
                                <div 
                                    key={entry.address}
                                    className={`flex items-center justify-between p-2 rounded-lg ${isCurrentUser ? 'bg-[#FFD700]/10 border border-[#FFD700]/30' : 'hover:bg-white/5'}`}
                                >
                                    <div className="flex items-center gap-3">
                                        <span className="w-8 flex items-center justify-center">
                                            {idx < 3 ? <RankBadge rank={idx + 1} size="sm" /> : <span className="font-mono text-white/50 text-sm">#{idx + 1}</span>}
                                        </span>
                                        <span className="text-white font-mono text-sm">
                                            {formatAddress(entry.address)}
                                        </span>
                                        {isCurrentUser && (
                                            <Badge className="bg-[#00FF66]/20 text-[#00FF66] text-xs">You</Badge>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <B4PLogo size="sm" />
                                        <span className="text-[#FFD700] font-bold">{entry.total_earned}</span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

// Transaction History
function TransactionHistory({ transactions }) {
    return (
        <Card className="bg-white/[0.02] border-white/5">
            <CardHeader className="pb-2">
                <CardTitle className="text-lg text-white flex items-center gap-2">
                    <History className="w-5 h-5 text-[#9B6FE8]" />
                    History
                </CardTitle>
            </CardHeader>
            <CardContent>
                {transactions.length === 0 ? (
                    <p className="text-white/40 text-center py-4">No transactions</p>
                ) : (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                        {transactions.slice(0, 10).map((tx) => (
                            <div 
                                key={tx.id}
                                className="flex items-center justify-between p-2 border-b border-white/5 last:border-0"
                            >
                                <div>
                                    <div className="text-white text-sm">
                                        {tx.type === 'earn' ? tx.action : tx.metadata?.item_name}
                                    </div>
                                    <div className="text-white/40 text-xs">
                                        {new Date(tx.created_at).toLocaleDateString()}
                                    </div>
                                </div>
                                <span className={`font-bold ${tx.amount > 0 ? 'text-[#00FF66]' : 'text-[#FF6B6B]'}`}>
                                    {tx.amount > 0 ? '+' : ''}{tx.amount} B4P
                                </span>
                            </div>
                        ))}
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

// AI Prediction Component
function AIPrediction({ address, onSpent }) {
    const [betDesc, setBetDesc] = useState('');
    const [category, setCategory] = useState('sports');
    const [prediction, setPrediction] = useState(null);
    const [predicting, setPredicting] = useState(false);
    const [history, setHistory] = useState([]);
    
    useEffect(() => {
        if (address) {
            fetch(`${API_URL}/api/b4p/predictions/${address}?limit=5`)
                .then(r => r.ok ? r.json() : [])
                .then(d => setHistory(d))
                .catch(() => {});
        }
    }, [address]);
    
    const handlePredict = async () => {
        if (!betDesc.trim()) return;
        setPredicting(true);
        setPrediction(null);
        try {
            const res = await fetch(`${API_URL}/api/b4p/predict`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ address, bet_description: betDesc, category })
            });
            const data = await res.json();
            if (data.success) {
                setPrediction(data.prediction);
                toast.success(`Prediction generated! -${data.b4p_spent} B4P`);
                onSpent?.();
            } else {
                toast.error(data.error || 'Prediction error');
            }
        } catch (error) {
            toast.error('Prediction error');
        } finally {
            setPredicting(false);
        }
    };
    
    const categories = [
        { value: 'sports', label: 'Sports' },
        { value: 'crypto', label: 'Crypto' },
        { value: 'politics', label: 'Politics' },
        { value: 'entertainment', label: 'Entertainment' },
        { value: 'general', label: 'Other' }
    ];
    
    return (
        <Card className="bg-gradient-to-br from-[#9B6FE8]/10 to-[#6F3FCA]/10 border-[#9B6FE8]/20">
            <CardHeader className="pb-2">
                <CardTitle className="text-lg text-white flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-[#9B6FE8]" />
                    AI Prediction
                </CardTitle>
                <CardDescription className="text-white/50">
                    Spend B4P for an AI analysis of your bet
                </CardDescription>
            </CardHeader>
            <CardContent>
                <div className="space-y-3">
                    <textarea
                        value={betDesc}
                        onChange={e => setBetDesc(e.target.value)}
                        placeholder="Describe the bet to analyze..."
                        className="w-full bg-white/5 border border-white/10 rounded-lg p-3 text-white text-sm resize-none h-20 placeholder:text-white/30 focus:outline-none focus:border-[#9B6FE8]/50"
                        data-testid="ai-prediction-input"
                    />
                    <div className="flex gap-2 flex-wrap">
                        {categories.map(cat => (
                            <button
                                key={cat.value}
                                onClick={() => setCategory(cat.value)}
                                className={`px-3 py-1 rounded-full text-xs transition-colors ${
                                    category === cat.value 
                                        ? 'bg-[#9B6FE8] text-white' 
                                        : 'bg-white/5 text-white/50 hover:bg-white/10'
                                }`}
                            >
                                {cat.label}
                            </button>
                        ))}
                    </div>
                    <Button
                        onClick={handlePredict}
                        disabled={!betDesc.trim() || predicting}
                        className="w-full bg-[#9B6FE8] text-white hover:bg-[#8B5FD8]"
                        data-testid="ai-predict-btn"
                    >
                        {predicting ? (
                            <><span className="animate-spin mr-2">&#9696;</span>Analyzing...</>
                        ) : (
                            <><Sparkles className="w-4 h-4 mr-2" />Analyze (costs B4P)</>
                        )}
                    </Button>
                    
                    {prediction && (
                        <div className="mt-4 p-4 rounded-lg bg-white/5 border border-[#9B6FE8]/20" data-testid="ai-prediction-result">
                            <div className="text-white text-sm whitespace-pre-wrap leading-relaxed">{prediction}</div>
                        </div>
                    )}
                    
                    {history.length > 0 && (
                        <div className="mt-3">
                            <div className="text-xs text-white/40 mb-2">Recent predictions</div>
                            {history.slice(0, 3).map((p) => (
                                <div key={p.id || p.created_at} className="p-2 mb-2 rounded bg-white/[0.03] border border-white/5">
                                    <div className="text-xs text-white/60 truncate">{p.bet_description}</div>
                                    <div className="text-xs text-white/30 mt-1">{new Date(p.created_at).toLocaleDateString()}</div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </CardContent>
        </Card>
    );
}


// Bridge Component - Off-chain to On-chain
const B4P_TOKEN = "0x92DE221A94C12920C896D08ebEfA6Bb98c8815aE";

function BridgeCard({ address, offChainBalance, onBridged }) {
    const [amount, setAmount] = useState('');
    const [bridging, setBridging] = useState(false);
    const [bridgeStatus, setBridgeStatus] = useState(null);
    
    useEffect(() => {
        if (address) {
            fetch(`${API_URL}/api/b4p/bridge/status/${address}`)
                .then(r => r.ok ? r.json() : null)
                .then(d => d && setBridgeStatus(d))
                .catch(() => {});
        }
    }, [address]);
    
    const handleBridge = async () => {
        const amt = parseFloat(amount);
        if (!amt || amt <= 0) return;
        setBridging(true);
        try {
            const res = await fetch(`${API_URL}/api/b4p/bridge/request`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ address, amount: amt })
            });
            const data = await res.json();
            if (data.success) {
                toast.success(data.message);
                setAmount('');
                onBridged?.();
                // Refresh status
                const sr = await fetch(`${API_URL}/api/b4p/bridge/status/${address}`);
                if (sr.ok) setBridgeStatus(await sr.json());
            } else {
                toast.error(data.error || 'Bridge failed');
            }
        } catch (e) {
            toast.error('Bridge error');
        } finally {
            setBridging(false);
        }
    };
    
    const pendingRequest = bridgeStatus?.requests?.find(r => r.status === 'pending' || r.status === 'approved');
    
    const statusColors = {
        pending: 'text-[#FFD700] bg-[#FFD700]/10',
        approved: 'text-[#00FF66] bg-[#00FF66]/10',
        completed: 'text-white/50 bg-white/5'
    };
    
    return (
        <Card className="bg-gradient-to-br from-[#FF6B00]/5 to-[#FFD700]/5 border-[#FF6B00]/20">
            <CardHeader className="pb-2">
                <CardTitle className="text-lg text-white flex items-center gap-2">
                    <ArrowDownUp className="w-5 h-5 text-[#FF6B00]" />
                    Bridge to On-Chain
                </CardTitle>
                <CardDescription className="text-white/50">
                    Convert off-chain B4P to ERC-20 tokens on Base
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 rounded-lg bg-white/5">
                        <div className="text-white/40 text-xs">Off-Chain</div>
                        <div className="text-white font-bold">{offChainBalance.toLocaleString()} B4P</div>
                    </div>
                    <div className="p-3 rounded-lg bg-[#FF6B00]/10">
                        <div className="text-[#FF6B00]/60 text-xs">On-Chain (Base)</div>
                        <a 
                            href={`https://basescan.org/token/${B4P_TOKEN}?a=${address}`}
                            target="_blank" rel="noopener noreferrer"
                            className="text-[#FF6B00] font-bold flex items-center gap-1 hover:underline"
                        >
                            View <ExternalLink className="w-3 h-3" />
                        </a>
                    </div>
                </div>
                
                {pendingRequest ? (
                    <div className={`p-3 rounded-lg ${statusColors[pendingRequest.status]} border border-current/20`}>
                        <div className="flex items-center justify-between">
                            <div>
                                <div className="text-sm font-semibold">
                                    {pendingRequest.status === 'pending' ? 'Bridge Pending' : 'Ready to Claim'}
                                </div>
                                <div className="text-xs opacity-70">{pendingRequest.amount} B4P</div>
                            </div>
                            {pendingRequest.status === 'approved' && (
                                <Button size="sm" className="bg-[#00FF66] text-[#0A0A0C]" data-testid="claim-onchain-btn">
                                    Claim On-Chain
                                </Button>
                            )}
                            {pendingRequest.status === 'pending' && (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            )}
                        </div>
                    </div>
                ) : (
                    <div className="space-y-2">
                        <div className="flex gap-2">
                            <Input
                                type="number"
                                value={amount}
                                onChange={e => setAmount(e.target.value)}
                                placeholder="Amount to bridge"
                                className="bg-white/5 border-white/10"
                                min="1"
                                max={offChainBalance}
                                data-testid="bridge-amount-input"
                            />
                            <Button
                                onClick={() => setAmount(String(Math.floor(offChainBalance)))}
                                size="sm" variant="outline"
                                className="border-white/10 text-white/50 shrink-0"
                            >
                                Max
                            </Button>
                        </div>
                        <Button
                            onClick={handleBridge}
                            disabled={!amount || parseFloat(amount) <= 0 || bridging}
                            className="w-full bg-[#FF6B00] text-white hover:bg-[#FF8533]"
                            data-testid="bridge-submit-btn"
                        >
                            {bridging ? (
                                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Bridging...</>
                            ) : (
                                <><ArrowDownUp className="w-4 h-4 mr-2" />Bridge to Base</>
                            )}
                        </Button>
                    </div>
                )}
                
                {/* Recent bridge history */}
                {bridgeStatus?.requests?.length > 0 && (
                    <div>
                        <div className="text-xs text-white/40 mb-2">Bridge History</div>
                        {bridgeStatus.requests.slice(0, 3).map((req) => (
                            <div key={req.bridge_id || req.created_at} className="flex items-center justify-between py-1.5 border-b border-white/5 last:border-0">
                                <span className="text-white/60 text-sm">{req.amount} B4P</span>
                                <Badge className={statusColors[req.status] || 'bg-white/5 text-white/50'} data-testid={`bridge-status-${i}`}>
                                    {req.status}
                                </Badge>
                            </div>
                        ))}
                    </div>
                )}
                
                <div className="text-xs text-white/30 text-center">
                    Contract: <a href={`https://basescan.org/token/${B4P_TOKEN}`} target="_blank" rel="noopener noreferrer" className="text-[#FF6B00]/50 hover:text-[#FF6B00]">{B4P_TOKEN.slice(0, 10)}...{B4P_TOKEN.slice(-6)}</a>
                </div>
            </CardContent>
        </Card>
    );
}

// Referral Card Component
function ReferralCard({ address, onClaimed }) {
    const [referralInfo, setReferralInfo] = useState(null);
    const [codeInput, setCodeInput] = useState('');
    const [claiming, setClaiming] = useState(false);
    const [copied, setCopied] = useState(false);
    const [searchParams] = useSearchParams();
    
    useEffect(() => {
        if (address) {
            fetch(`${API_URL}/api/b4p/referral/${address}`)
                .then(r => r.ok ? r.json() : null)
                .then(d => d && setReferralInfo(d))
                .catch(() => {});
        }
    }, [address]);
    
    // Auto-fill referral code from URL param
    useEffect(() => {
        const ref = searchParams.get('ref');
        if (ref && !codeInput) setCodeInput(ref);
    }, [searchParams]);
    
    const referralLink = referralInfo ? `${getPublicOrigin()}/b4p?ref=${referralInfo.referral_code}` : '';
    
    const handleCopy = () => {
        navigator.clipboard.writeText(referralLink);
        setCopied(true);
        toast.success('Link copied!');
        setTimeout(() => setCopied(false), 2000);
    };
    
    const handleShareX = () => {
        const text = `Join Bet4Peer and earn 2 B4P welcome bonus! Use my referral link:`;
        window.open(`https://x.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(referralLink)}`, '_blank');
    };
    
    const handleClaim = async () => {
        if (!codeInput.trim()) return;
        setClaiming(true);
        try {
            const res = await fetch(`${API_URL}/api/b4p/referral/claim`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ address, referral_code: codeInput.trim() })
            });
            const data = await res.json();
            if (data.success) {
                toast.success(data.message);
                onClaimed?.();
                // Refresh referral info
                const r = await fetch(`${API_URL}/api/b4p/referral/${address}`);
                if (r.ok) setReferralInfo(await r.json());
            } else {
                toast.error(data.error || 'Error');
            }
        } catch (e) {
            toast.error('Referral error');
        } finally {
            setClaiming(false);
        }
    };
    
    return (
        <Card className="bg-gradient-to-br from-[#00FF66]/5 to-[#00C853]/5 border-[#00FF66]/20">
            <CardHeader className="pb-2">
                <CardTitle className="text-lg text-white flex items-center gap-2">
                    <UserPlus className="w-5 h-5 text-[#00FF66]" />
                    Referral
                </CardTitle>
                <CardDescription className="text-white/50">
                    Invite friends and earn 5 B4P per referral
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
                {/* Your referral link */}
                {referralInfo && (
                    <div>
                        <div className="text-xs text-white/40 mb-2">Your referral link</div>
                        <div className="flex gap-2">
                            <div className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 font-mono text-xs text-white/70 truncate" data-testid="referral-link">
                                {referralLink}
                            </div>
                            <Button size="sm" onClick={handleCopy} className="bg-[#00FF66] text-[#0A0A0C] hover:bg-[#00CC52] shrink-0" data-testid="copy-referral-btn">
                                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                            </Button>
                            <Button size="sm" onClick={handleShareX} variant="outline" className="border-white/10 text-white/70 shrink-0" data-testid="share-referral-x">
                                <Share2 className="w-4 h-4" />
                            </Button>
                        </div>
                    </div>
                )}
                
                {/* Stats */}
                {referralInfo && (
                    <div className="grid grid-cols-2 gap-3">
                        <div className="p-3 rounded-lg bg-white/5">
                            <div className="text-white/40 text-xs">Referrals</div>
                            <div className="text-white font-bold text-xl" data-testid="referral-count">{referralInfo.referral_count}</div>
                        </div>
                        <div className="p-3 rounded-lg bg-white/5">
                            <div className="text-white/40 text-xs">B4P Earned</div>
                            <div className="text-[#00FF66] font-bold text-xl" data-testid="referral-earned">+{referralInfo.total_earned_from_referrals}</div>
                        </div>
                    </div>
                )}
                
                {/* Enter a referral code */}
                <div>
                    <div className="text-xs text-white/40 mb-2">Have a referral code?</div>
                    <div className="flex gap-2">
                        <Input
                            value={codeInput}
                            onChange={e => setCodeInput(e.target.value)}
                            placeholder="Code (8 characters)"
                            className="bg-white/5 border-white/10 text-sm"
                            maxLength={8}
                            data-testid="referral-code-input"
                        />
                        <Button
                            onClick={handleClaim}
                            disabled={!codeInput.trim() || claiming}
                            className="bg-[#FFD700] text-[#0A0A0C] hover:bg-[#FFE44D] shrink-0"
                            data-testid="claim-referral-btn"
                        >
                            {claiming ? 'Sending...' : 'Claim'}
                        </Button>
                    </div>
                </div>
                
                {/* Recent referrals */}
                {referralInfo?.recent_referrals?.length > 0 && (
                    <div>
                        <div className="text-xs text-white/40 mb-2">Recent referrals</div>
                        {referralInfo.recent_referrals.slice(0, 3).map((r) => (
                            <div key={r.referee || r.created_at} className="flex items-center justify-between py-1.5 border-b border-white/5 last:border-0">
                                <span className="text-white/60 text-sm font-mono">{formatAddress(r.referee)}</span>
                                <span className="text-[#00FF66] text-xs">{new Date(r.created_at).toLocaleDateString()}</span>
                            </div>
                        ))}
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

// Main B4P Page
export function B4PPage() {
    const { address, isConnected } = useWeb3();
    const [loading, setLoading] = useState(true);
    const [balance, setBalance] = useState({ balance: 0, total_earned: 0, total_spent: 0, halving_info: { current_phase: 1, current_multiplier: 1, total_distributed: 0, next_halving_at: 10000, progress_to_halving: 0 } });
    const [rewards, setRewards] = useState({ rewards: {}, multiplier: 1 });
    const [shop, setShop] = useState([]);
    const [leaderboard, setLeaderboard] = useState([]);
    const [transactions, setTransactions] = useState([]);
    const [purchasing, setPurchasing] = useState(false);
    
    useEffect(() => {
        fetchData();
    }, [address]);
    
    const fetchData = async () => {
        setLoading(true);
        try {
            // Fetch rewards info
            const rewardsRes = await fetch(`${API_URL}/api/b4p/rewards`);
            if (rewardsRes.ok) setRewards(await rewardsRes.json());
            
            // Fetch shop items
            const shopRes = await fetch(`${API_URL}/api/b4p/shop`);
            if (shopRes.ok) setShop(await shopRes.json());
            
            // Fetch leaderboard
            const lbRes = await fetch(`${API_URL}/api/b4p/leaderboard?limit=10`);
            if (lbRes.ok) setLeaderboard(await lbRes.json());
            
            // Fetch global stats (for halving info when not connected)
            const statsRes = await fetch(`${API_URL}/api/b4p/stats`);
            if (statsRes.ok) {
                const statsData = await statsRes.json();
                // Update halving info from global stats
                setBalance(prev => ({
                    ...prev,
                    halving_info: {
                        current_phase: statsData.current_phase,
                        current_multiplier: statsData.current_multiplier,
                        total_distributed: statsData.total_distributed,
                        next_halving_at: statsData.next_halving_at,
                        progress_to_halving: statsData.next_halving_at ? 
                            Math.round((statsData.total_distributed % 10000) / 10000 * 1000) / 10 : 100
                    }
                }));
            }
            
            // Fetch user-specific data if connected
            if (address) {
                const balanceRes = await fetch(`${API_URL}/api/b4p/balance/${address}`);
                if (balanceRes.ok) setBalance(await balanceRes.json());
                
                const txRes = await fetch(`${API_URL}/api/b4p/transactions/${address}?limit=10`);
                if (txRes.ok) setTransactions(await txRes.json());
            }
        } catch (error) {
            console.error('Failed to fetch B4P data:', error);
        } finally {
            setLoading(false);
        }
    };
    
    const handleClaimDaily = async () => {
        if (!address) {
            toast.error('Connect your wallet');
            return;
        }
        
        try {
            const res = await fetch(`${API_URL}/api/b4p/daily-login?address=${address}`, { method: 'POST' });
            const data = await res.json();
            
            if (data.success) {
                toast.success(`+${data.earned} B4P daily bonus!`);
                fetchData();
            } else {
                toast.error(data.error || 'Already claimed today');
            }
        } catch (error) {
            toast.error('Failed to claim daily bonus');
        }
    };
    
    const handleBuyItem = async (itemId) => {
        if (!address) {
            toast.error('Connect your wallet');
            return;
        }
        
        setPurchasing(true);
        try {
            const res = await fetch(`${API_URL}/api/b4p/spend?address=${address}&item_id=${itemId}`, { method: 'POST' });
            const data = await res.json();
            
            if (data.success) {
                toast.success(`Purchase successful! -${data.spent} B4P`);
                fetchData();
            } else {
                toast.error(data.error || 'Purchase failed');
            }
        } catch (error) {
            toast.error('Purchase error');
        } finally {
            setPurchasing(false);
        }
    };
    
    if (loading) {
        return (
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <Skeleton className="h-40 w-full mb-6 bg-white/5" />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Skeleton className="h-60 bg-white/5" />
                    <Skeleton className="h-60 bg-white/5" />
                </div>
            </div>
        );
    }
    
    return (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 page-enter" data-testid="b4p-page">
            {/* Header */}
            <div className="text-center mb-8 animate-fade-in-down">
                <div className="flex items-center justify-center gap-4 mb-4">
                    <B4PLogo size="lg" />
                    <div>
                        <h1 className="text-3xl md:text-4xl font-bold text-white">
                            <span className="text-[#FFD700]">B4P</span> Token
                        </h1>
                        <p className="text-white/50">Bet4PeerToken - Earn while you bet!</p>
                    </div>
                </div>
                <a 
                    href="https://basescan.org/token/0x92DE221A94C12920C896D08ebEfA6Bb98c8815aE"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block"
                >
                    <Badge className="bg-[#FFD700]/20 text-[#FFD700] hover:bg-[#FFD700]/30 cursor-pointer transition-colors" data-testid="b4p-contract-badge">
                        Contract: 0x92DE...815aE (Base)
                    </Badge>
                </a>
            </div>
            
            {/* Balance Card (if connected) */}
            {isConnected && (
                <div className="mb-8">
                    <BalanceCard 
                        balance={balance.balance}
                        totalEarned={balance.total_earned}
                        totalSpent={balance.total_spent}
                        onClaimDaily={handleClaimDaily}
                    />
                </div>
            )}
            
            {/* Not connected message */}
            {!isConnected && (
                <Card className="bg-[#FFD700]/10 border-[#FFD700]/30 mb-8">
                    <CardContent className="p-6 text-center">
                        <Coins className="w-12 h-12 text-[#FFD700] mx-auto mb-4" />
                        <h3 className="text-xl font-bold text-white mb-2">Connect your wallet</h3>
                        <p className="text-white/60">View your B4P balance and earn rewards</p>
                    </CardContent>
                </Card>
            )}
            
            {/* Main Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Left Column */}
                <div className="lg:col-span-2 space-y-6">
                    {/* Halving Info */}
                    {balance.halving_info && (
                        <div className="animate-fade-in-up delay-1">
                            <HalvingCard halvingInfo={balance.halving_info} />
                        </div>
                    )}
                    
                    {/* Shop */}
                    <div className="animate-fade-in-up delay-2">
                        <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                            <ShoppingBag className="w-5 h-5 text-[#FFD700]" />
                            B4P Shop
                            {balance.halving_info?.current_phase > 1 && (
                                <Badge className="bg-[#00FF66]/20 text-[#00FF66] text-xs ml-2">
                                    Reduced prices Phase {balance.halving_info.current_phase}
                                </Badge>
                            )}
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {shop.map((item, index) => (
                                <div key={item.id} className={`animate-scale-in delay-${index + 1}`}>
                                    <ShopItemCard 
                                        item={item}
                                        onBuy={handleBuyItem}
                                        userBalance={balance.balance}
                                        isPurchasing={purchasing}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                    
                    {/* AI Predictions */}
                    {isConnected && (
                        <div className="animate-fade-in-up delay-4">
                            <AIPrediction address={address} onSpent={fetchData} />
                        </div>
                    )}
                </div>
                
                {/* Right Column */}
                <div className="space-y-6">
                    {isConnected && <div className="animate-fade-in-right delay-1"><BridgeCard address={address} offChainBalance={balance.balance} onBridged={fetchData} /></div>}
                    {isConnected && <div className="animate-fade-in-right delay-2"><ReferralCard address={address} onClaimed={fetchData} /></div>}
                    <div className="animate-fade-in-right delay-3"><RewardsCard rewards={rewards.rewards} multiplier={rewards.multiplier} /></div>
                    <div className="animate-fade-in-right delay-4"><B4PLeaderboard leaderboard={leaderboard} currentAddress={address} /></div>
                    {isConnected && <div className="animate-fade-in-right delay-5"><TransactionHistory transactions={transactions} /></div>}
                </div>
            </div>
        </div>
    );
}
